<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";

import type { OperatorRole, PublicConfig, RemoteSession } from "@/api/types";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";
import { useVisualViewport } from "@/composables/useVisualViewport";
import { useSessionKeepalive } from "@/composables/useSessionKeepalive";
import { appVersion } from "@/version";

const props = defineProps<{ config: PublicConfig }>();
const { t } = useI18n();
const auth = useAuthStore();
const router = useRouter();
const route = useRoute();
const viewport = useVisualViewport();
useSessionKeepalive();

type NavItem = {
  to: string;
  key: string;
  modes?: PublicConfig["mode"][];
  roles?: OperatorRole[];
};

// Primary destinations. NX is intentionally absent: the web NX view is
// reached from its endpoint (admin), not a top-level item. Items behind a
// role/mode gate the operator can't reach are also hidden from the menu.
const ALL_NAV: NavItem[] = [
  { to: "/", key: "nav.dashboard" },
  { to: "/new", key: "nav.new" },
  { to: "/chat", key: "nav.chat" },
  { to: "/workers", key: "nav.workers", roles: ["owner", "admin"] },
  { to: "/tunnels", key: "nav.tunnels" },
  { to: "/split", key: "nav.split" },
  { to: "/port-console", key: "nav.port_console" },
  { to: "/admin", key: "nav.admin", roles: ["owner", "admin"] },
  { to: "/billing", key: "nav.billing", modes: ["saas"] },
  { to: "/account", key: "nav.account", modes: ["saas"] },
  { to: "/status", key: "nav.status", modes: ["saas"] },
  { to: "/owner", key: "nav.owner", modes: ["saas"], roles: ["owner"] },
];

const navItems = computed(() =>
  ALL_NAV.filter((item) => {
    const modeOk = !item.modes || item.modes.includes(props.config.mode);
    const roleOk = !item.roles || auth.hasRole(item.roles);
    return modeOk && roleOk;
  }),
);

const operator = computed(() => auth.operator());

// Left off-canvas drawer — hidden by default on every viewport, opened by
// the ☰ button, closed by a nav click, the backdrop, or Esc.
const drawerOpen = ref(false);
// Running sessions shown in the drawer — fetched fresh each time it opens so
// the list is current regardless of which view is mounted (no shared stream).
const sessions = ref<RemoteSession[]>([]);
async function loadSessions() {
  try {
    const r = await api.sessions.listAll();
    sessions.value = r.sessions.filter((s) => s.status !== "stopped");
  } catch {
    sessions.value = [];
  }
}
function toggleDrawer() {
  drawerOpen.value = !drawerOpen.value;
  if (drawerOpen.value) void loadSessions();
}
function closeDrawer() {
  drawerOpen.value = false;
}
// Close on route change so navigating never leaves the overlay up.
watch(
  () => route.fullPath,
  () => closeDrawer(),
);

async function doLogout() {
  closeDrawer();
  await auth.logout();
  await router.push({ name: "login" });
}
</script>

<template>
  <div class="shell" :style="viewport.isTouch.value ? viewport.fixedStyle.value : undefined">
    <header class="shell__header">
      <button
        v-if="operator"
        type="button"
        class="shell__menu-btn"
        :aria-expanded="drawerOpen"
        :aria-label="t('a11y.menu')"
        @click="toggleDrawer"
      >
        ☰
      </button>
      <RouterLink to="/" class="shell__brand">
        <strong>{{ t("app.title") }}</strong>
        <span class="shell__mode" :data-mode="config.mode">{{ t(`mode.${config.mode}`) }}</span>
        <span class="shell__version" :title="t('settings.app_version')">v{{ appVersion }}</span>
      </RouterLink>
      <div class="shell__spacer"></div>
      <div v-if="operator" class="shell__who">
        <span class="shell__who-email">{{ operator.email }}</span>
        <span class="shell__who-role">{{ t(`role.${operator.role}`) }}</span>
      </div>
      <RouterLink v-else :to="{ name: 'login' }" class="shell__login-link">
        {{ t("nav.login") }}
      </RouterLink>
    </header>

    <!-- Backdrop + left drawer (overlay) — only for authenticated operators;
         the login/anonymous shell shows no navigation. -->
    <div v-if="drawerOpen && operator" class="shell__backdrop" @click="closeDrawer"></div>
    <aside
      v-if="operator"
      class="shell__drawer"
      :class="{ 'shell__drawer--open': drawerOpen }"
      :aria-label="t('a11y.navigation')"
    >
      <nav class="shell__nav">
        <RouterLink
          v-for="item in navItems"
          :key="item.to"
          :to="item.to"
          class="shell__nav-link"
          @click="closeDrawer"
        >
          {{ t(item.key) }}
        </RouterLink>
      </nav>

      <!-- Running sessions — jump straight to a terminal without the dashboard. -->
      <div v-if="sessions.length" class="shell__sessions">
        <div class="shell__group-label">{{ t("nav.sessions") }}</div>
        <RouterLink
          v-for="s in sessions"
          :key="s.id"
          :to="`/session/${s.id}`"
          class="shell__nav-link shell__session"
          @click="closeDrawer"
        >
          <span class="shell__dot" :data-tone="s.status === 'running' ? 'ok' : 'warn'"></span>
          <span class="shell__session-name">{{ s.name || s.engine }}</span>
        </RouterLink>
      </div>

      <div v-if="operator" class="shell__drawer-foot">
        <RouterLink class="shell__nav-link" :to="{ name: 'settings' }" @click="closeDrawer">
          {{ t("nav.settings") }}
        </RouterLink>
        <button type="button" class="shell__logout" @click="doLogout">
          {{ t("user.logout") }}
        </button>
        <div class="shell__drawer-user">
          <span>{{ t("settings.app_version") }}: {{ appVersion }}</span>
          <span>{{ operator.email }}</span>
          <span class="shell__who-role"
            >{{ t(`role.${operator.role}`) }} · org {{ operator.organization_id.slice(0, 8) }}</span
          >
        </div>
      </div>
    </aside>

    <main class="shell__main">
      <!-- Re-key the terminal per session so navigating A→B remounts it (the
           same component is otherwise reused, leaving the old session live).
           Everything else keeps a stable key so nested layouts (admin) aren't
           remounted on every sub-navigation. -->
      <RouterView v-slot="{ Component }">
        <component :is="Component" :key="route.name === 'session' ? route.fullPath : 'app'" />
      </RouterView>
    </main>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
}
.shell__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0.85rem;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-bg-elev);
  flex: 0 0 auto;
  z-index: 30;
}
.shell__menu-btn {
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  line-height: 1;
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--color-fg);
  cursor: pointer;
}
.shell__brand {
  display: flex;
  align-items: baseline;
  gap: 0.6rem;
  color: var(--color-fg);
}
.shell__mode {
  font-size: 0.72rem;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  background: var(--color-border);
}
.shell__mode[data-mode="saas"] {
  background: rgba(108, 180, 255, 0.15);
  color: var(--color-accent);
}
.shell__version {
  font-size: 0.72rem;
  color: var(--color-fg-dim);
  font-family: var(--font-mono);
}
.shell__spacer {
  flex: 1;
}
.shell__who {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.2;
}
.shell__who-email {
  font-size: 0.85rem;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shell__who-role {
  font-size: 0.7rem;
  color: var(--color-fg-dim);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.shell__login-link {
  padding: 0.35rem 0.8rem;
  border-radius: 4px;
  background: var(--color-accent);
  color: #fff;
  font-weight: 500;
}

/* ---- left drawer + backdrop -------------------------------------- */
.shell__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 35;
}
.shell__drawer {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 16rem;
  max-width: 84vw;
  background: var(--color-bg-elev);
  border-right: 1px solid var(--color-border);
  box-shadow: 2px 0 16px rgba(0, 0, 0, 0.35);
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 0.75rem;
  padding-top: calc(0.75rem + env(safe-area-inset-top));
  transform: translateX(-100%);
  transition: transform 0.18s ease;
}
.shell__drawer--open {
  transform: translateX(0);
}
.shell__nav {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}
.shell__nav-link {
  display: block;
  padding: 0.7rem 0.75rem;
  border-radius: 6px;
  color: var(--color-fg);
  min-height: 44px;
  line-height: 1.6;
}
.shell__nav-link.router-link-active {
  background: var(--color-border);
}
.shell__sessions {
  margin-top: 0.6rem;
  border-top: 1px solid var(--color-border);
  padding-top: 0.4rem;
  overflow-y: auto;
}
.shell__group-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-fg-dim);
  padding: 0.3rem 0.75rem;
}
.shell__session {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  min-height: 40px;
}
.shell__dot {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  flex: 0 0 auto;
  background: var(--color-fg-dim);
}
.shell__dot[data-tone="ok"] {
  background: var(--color-ok);
}
.shell__dot[data-tone="warn"] {
  background: var(--color-warn);
}
.shell__session-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.shell__drawer-foot {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  border-top: 1px solid var(--color-border);
  padding-top: 0.6rem;
}
.shell__logout {
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-bg);
  color: var(--color-fg);
  cursor: pointer;
  text-align: left;
  min-height: 44px;
}
.shell__drawer-user {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.4rem 0.75rem 0.2rem;
  font-size: 0.78rem;
  color: var(--color-fg-dim);
  overflow-wrap: anywhere;
}
.shell__main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
}

@media (max-width: 640px) {
  .shell__version {
    display: none;
  }
  /* Email/role lives in the drawer footer on mobile — drop it from the bar
     so the top row always fits one line (no horizontal scroll). */
  .shell__who {
    display: none;
  }
  .shell__main {
    padding: 0.85rem;
  }
}
</style>
